<template>
  <div>
    <ul class="nav nav-tabs" role="tablist">
      <Tab v-for="(priceGroup, index) in servicePriceGroupList" v-bind:first="index == 0" :tabid="`tab-service-pricing-${index}`" :key="index">
        {{priceGroup.name}}
      </Tab>
    </ul>
    <div class="tab-content">
      <TabPage v-for="(priceGroup, index) in servicePriceGroupList" v-bind:first="index == 0" :tabid="`tab-service-pricing-${index}`" :key="index">

        <div class="row">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th width="25%">Thickness</th>
                  <th>Active</th>
                  <th v-html="priceHeader"></th>
                  <th><Tooltip label="Minimum price per individual item. This amount is not multiplied by sales rates" helpStyle>Min Price Per Item</Tooltip></th>
                  <th><Tooltip label="Minimum price per group. This amount is not multiplied by sales rates" helpStyle>Min Price Per Group</Tooltip></th>
                  <th><Tooltip label="Minimum invoice charge when this stock item is included. This amount is not multiplied by sales rates" helpStyle>Min Invoice Charge</Tooltip></th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="priceRange in priceGroup.servicePriceRanges" :key="priceRange.id">
                  <td class="font-weight-bold">
                    {{priceRange.name}}
                    <span v-if="priceRange.isPendingUpdate" class="pricing-pending-update">
                      <Tooltip label="Price updates pending"><i class="far fa-clock"></i></Tooltip>
                    </span>
                  </td>
                  <td>
                    <FormCheckbox v-model="priceRange.isActive" @change="priceRange.isActive = $event; priceUpdated()" v-show="editing && !priceRange.isPendingUpdate" />
                    <span v-show="!editing || priceRange.isPendingUpdate" v-html="dataDisplayTableBoolean(priceRange.isActive, {showFalse:true})"></span>
                  </td>
                  <td>
                    <FormInputStockPrice :uomType="uomType" v-model="priceRange.price" @input="priceRange.price = $event; priceUpdated()" v-show="editing && !priceRange.isPendingUpdate" />
                    <span v-show="!editing || priceRange.isPendingUpdate">{{priceRange.price | formatPriceWithUOM(uomType)}}</span>
                    <span v-show="editing && priceRange.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
                  </td>
                  <td>
                    <FormInputCurrency v-model="priceRange.minChargeItemPrice" @input="priceRange.minChargeItemPrice = $event; priceUpdated()" v-show="editing && !priceRange.isPendingUpdate" />
                    <span v-show="!editing || priceRange.isPendingUpdate">{{priceRange.minChargeItemPrice | formatCurrency(2, true)}}</span>
                    <span v-show="editing && priceRange.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
                  </td>
                  <td>
                    <FormInputCurrency v-model="priceRange.minChargePrice" @input="priceRange.minChargePrice = $event; priceUpdated()" v-show="editing && !priceRange.isPendingUpdate" />
                    <span v-show="!editing || priceRange.isPendingUpdate">{{priceRange.minChargePrice | formatCurrency(2, true)}}</span>
                    <span v-show="editing && priceRange.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
                  </td>
                  <td>
                    <FormInputCurrency
                      :value="priceRange.minChargeInvoice"
                      @input="priceRange.minChargeInvoice = $event; priceUpdated()"
                      v-show="editing && !priceRange.isPendingUpdate" />
                    <span v-show="(!editing || priceRange.isPendingUpdate)">{{priceRange.minChargeInvoice | formatCurrency(2, true)}}</span>
                    <span v-show="editing && priceRange.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
                  </td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!editing && priceRange.isPendingUpdate && userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval)">
                      <DropdownMenuItem :link="`/pricing/requests/${priceRange.stockPriceRequestGroupId}`" icon="fa-clock" label="Show Pending Prices">Show Pending Prices</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-lg-12">
            <div class="btn-group float-right">
              <button class="btn btn-xs btn-white" :class="[isSalesMatrixDisplayPrice ? 'active' : '']" @click.prevent="isSalesMatrixDisplayPrice=true">Price</button>
              <button class="btn btn-xs btn-white" :class="[!isSalesMatrixDisplayPrice ? 'active' : '']" @click.prevent="isSalesMatrixDisplayPrice=false">Diff</button>
            </div>
            <h3>Sales Matrix</h3>
          </div>
        </div>

        <StockPricingMatrix
          :priceCategoryList="priceGroup.servicePriceRanges"
          :displayPrice="isSalesMatrixDisplayPrice"
          :uomType="uomType"
        />
      </TabPage>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as Types from '@/store/types'
import Utils from '@/utils/utils'
import DataDisplayMixin from '@/mixins/dataDisplay'
import StaticDataTable from "@/components/table/staticDataTable.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"
import StockPricingMatrix from '@/modules/pricing/components/stockPricingMatrix.vue'
import FormInputStockPrice from '@/modules/pricing/components/formInputStockPrice'

export default {
  name: "SalesServicePricingEditor",
  mixins: [DataDisplayMixin],
  components: {
    StaticDataTable,
    StockPricingMatrix,
    FormInputStockPrice,
    Tab,
    TabPage
  },
  props: {
    priceGroupList: Array,
    uomType: String,
    editing: Boolean
  },
  watch: {
    priceGroupList: function(newVal) { 
      this.$data.servicePriceGroupList = _.cloneDeep(newVal)
    }
  },
  computed: {
    priceHeader() {
      if(this.uomType == Types.UOMTypes.percentOfLineItem || this.uomType == Types.UOMTypes.percentOfOrder)
        return Utils.getLookupName(Types.UOMTypeLookup, this.uomType)

      let uomDisplay = Utils.getLookupName(Types.UOMTypeUnitsLookup, this.uomType)
      return `Price / ${uomDisplay}`
    }
  },
  methods: {
    priceUpdated() {
      this.$emit('update', this.$data.servicePriceGroupList)
    }
  },
  data() {
    return {
      isSalesMatrixDisplayPrice: true,
      servicePriceGroupList: _.cloneDeep(this.$props.priceGroupList)
    }
  },  
}
</script>